import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ease } from "@/lib/motion";
import { LucideIcon, Check, ShieldCheck, AlertTriangle, ChevronRight } from "lucide-react";

export interface MatrixCapability {
  id: string;
  name: string;
  icon?: LucideIcon;
  description: string;
  outcomes: string[];
  decisions: string[];
  failuresPrevented: string[];
}

export interface MatrixCategory {
  id: string;
  name: string;
  capabilities: MatrixCapability[];
}

interface CapabilityMatrixProps {
  title?: string;
  label?: string;
  description?: string;
  categories: MatrixCategory[];
}

const CapabilityMatrix = ({
  title = "Capability depth.",
  label = "Capabilities",
  description,
  categories,
}: CapabilityMatrixProps) => {
  const [activeCapability, setActiveCapability] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(categories[0]?.id || "");

  const currentCategory = categories.find((c) => c.id === activeCategory);
  const currentCapability = currentCategory?.capabilities.find((cap) => cap.id === activeCapability);

  return (
    <section className="py-10 md:py-14 lg:py-20 border-t border-border">
      <div className="container-wide">
        {/* Header */}
        <motion.div
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5, ease }}
        >
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">
            {label}
          </span>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-3">
            {title}
          </h2>
          {description && (
            <p className="text-muted-foreground">{description}</p>
          )}
        </motion.div>

        {/* Category Tabs */}
        {categories.length > 1 && (
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2 -mx-6 px-6 lg:mx-0 lg:px-0">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setActiveCapability(null);
                }}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200
                  ${activeCategory === category.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                  }
                `}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}

        {/* Matrix Grid */}
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12">
          {/* Capability List */}
          <div className="space-y-2">
            {currentCategory?.capabilities.map((capability, index) => {
              const isActive = activeCapability === capability.id;
              const Icon = capability.icon;

              return (
                <motion.button
                  key={capability.id}
                  onClick={() => setActiveCapability(isActive ? null : capability.id)}
                  className={`
                    w-full text-left p-4 rounded-xl border transition-all duration-300
                    ${isActive
                      ? "bg-card border-primary/30 shadow-sm"
                      : "border-border hover:border-primary/20 hover:bg-muted/30"
                    }
                  `}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * index, duration: 0.4, ease }}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    {Icon && (
                      <div
                        className={`
                          w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300
                          ${isActive ? "bg-primary/10" : "bg-muted"}
                        `}
                      >
                        <Icon
                          className={`w-5 h-5 transition-colors duration-300 ${isActive ? "text-primary" : "text-muted-foreground"}`}
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3
                          className={`font-medium transition-colors duration-300 ${isActive ? "text-foreground" : "text-foreground/80"}`}
                        >
                          {capability.name}
                        </h3>
                        <motion.div
                          animate={{ rotate: isActive ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-muted-foreground flex-shrink-0"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </motion.div>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {capability.description}
                      </p>
                    </div>
                  </div>

                  {/* Mobile Expanded Content */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease }}
                        className="lg:hidden overflow-hidden"
                      >
                        <div className="pt-4 mt-4 border-t border-border space-y-4">
                          {/* Outcomes */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <Check className="w-4 h-4 text-primary" />
                              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                Outcomes
                              </span>
                            </div>
                            <ul className="space-y-1.5">
                              {capability.outcomes.map((outcome) => (
                                <li key={outcome} className="text-sm text-foreground pl-6">
                                  {outcome}
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Decisions Enabled */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <ShieldCheck className="w-4 h-4 text-accent" />
                              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                Decisions Enabled
                              </span>
                            </div>
                            <ul className="space-y-1.5">
                              {capability.decisions.map((decision) => (
                                <li key={decision} className="text-sm text-foreground pl-6">
                                  {decision}
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Failures Prevented */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                Failures Prevented
                              </span>
                            </div>
                            <ul className="space-y-1.5">
                              {capability.failuresPrevented.map((failure) => (
                                <li key={failure} className="text-sm text-foreground pl-6">
                                  {failure}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>

          {/* Desktop Detail Panel */}
          <div className="hidden lg:block sticky top-24 self-start">
            <AnimatePresence mode="wait">
              {currentCapability ? (
                <motion.div
                  key={currentCapability.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.35, ease }}
                  className="bg-card border border-border rounded-2xl p-8"
                >
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-6 pb-6 border-b border-border">
                    {currentCapability.icon && (
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <currentCapability.icon className="w-6 h-6 text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">
                        {currentCapability.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {currentCapability.description}
                      </p>
                    </div>
                  </div>

                  {/* Outcomes */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Check className="w-4 h-4 text-primary" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Outcomes Delivered
                      </span>
                    </div>
                    <ul className="space-y-2 pl-9">
                      {currentCapability.outcomes.map((outcome, index) => (
                        <motion.li
                          key={outcome}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * index, duration: 0.3 }}
                          className="text-sm text-foreground"
                        >
                          {outcome}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Decisions Enabled */}
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
                        <ShieldCheck className="w-4 h-4 text-accent" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Decisions This Enables
                      </span>
                    </div>
                    <ul className="space-y-2 pl-9">
                      {currentCapability.decisions.map((decision, index) => (
                        <motion.li
                          key={decision}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 + 0.05 * index, duration: 0.3 }}
                          className="text-sm text-foreground"
                        >
                          {decision}
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Failures Prevented */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                      </div>
                      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                        Failures This Prevents
                      </span>
                    </div>
                    <ul className="space-y-2 pl-9">
                      {currentCapability.failuresPrevented.map((failure, index) => (
                        <motion.li
                          key={failure}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.15 + 0.05 * index, duration: 0.3 }}
                          className="text-sm text-foreground"
                        >
                          {failure}
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-muted/30 border border-dashed border-border rounded-2xl p-12 flex flex-col items-center justify-center text-center min-h-[400px]"
                >
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <ChevronRight className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-muted-foreground">
                    Select a capability to see details
                  </p>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    Outcomes, decisions enabled, and failures prevented
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilityMatrix;